import React, { useState } from "react";
import { AuthFrame } from "../components/AuthFrame.jsx";
import { postJson, responseMessage } from "../lib/api.js";
import { getRecaptchaToken } from "../lib/recaptcha.js";

const portalPaths = {
  Admin: "/Admin/Dashboard",
  Client: "/Client/Dashboard",
  Supplier: "/Supplier/Dashboard"
};

function PortalPicker({ roles }) {
  if (!roles.length) return null;
  return (
    <div className="react-portal-backdrop" role="dialog" aria-modal="true" aria-labelledby="portal-picker-title">
      <div className="react-portal-card">
        <h3 id="portal-picker-title">Select Workspace</h3>
        {roles.map((role) => (
          <button
            className="react-portal-option"
            type="button"
            key={role}
            onClick={() => window.location.assign(portalPaths[role] || "/")}
          >
            {role} Portal
          </button>
        ))}
      </div>
    </div>
  );
}

export function LoginPage() {
  const [mode, setMode] = useState("login");
  const [forgotMode, setForgotMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [confirmationEmail, setConfirmationEmail] = useState("");
  const [resendLabel, setResendLabel] = useState("Resend Confirmation Email");
  const [registerError, setRegisterError] = useState("");
  const [registerSuccess, setRegisterSuccess] = useState("");
  const [forgotStatus, setForgotStatus] = useState("");
  const [portalRoles, setPortalRoles] = useState([]);

  async function handleLogin(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const fields = new FormData(form);
    const email = String(fields.get("userName") || "");
    setBusy(true);
    setLoginError("");
    setConfirmationEmail("");

    try {
      const token = await getRecaptchaToken("login");
      if (!token && (window.RECAPTCHA_SITE_KEY || import.meta.env.VITE_RECAPTCHA_SITE_KEY)) {
        throw new Error("Security check failed. Please refresh.");
      }

      const { response, data } = await postJson("/api/auth/login", {
        userName: email,
        password: String(fields.get("password") || ""),
        recaptchaToken: token || "skip"
      });

      if (!response.ok) {
        if (data?.requiresConfirmation) {
          setLoginError(data.error || "Email not confirmed. Please check your inbox.");
          setConfirmationEmail(email);
          return;
        }
        throw new Error(responseMessage(data, "Login failed"));
      }

      const roles = Array.isArray(data?.roles) ? data.roles : [];
      if (data?.isPending) window.alert("Your account is pending approval.");

      if (roles.length > 1) {
        setPortalRoles(roles);
      } else if (roles.length === 1) {
        window.location.assign(portalPaths[roles[0]] || "/");
      } else {
        window.location.assign("/");
      }
    } catch (error) {
      setLoginError(error.message || "Login failed");
    } finally {
      setBusy(false);
    }
  }

  async function resendConfirmation() {
    setResendLabel("Sending...");
    try {
      const token = await getRecaptchaToken("resend_confirmation");
      const { response, data } = await postJson("/api/auth/resend-confirmation", {
        email: confirmationEmail,
        recaptchaToken: token || "skip"
      });
      if (!response.ok) throw new Error(responseMessage(data, "Failed to send."));
      setResendLabel(data?.message || "Email sent successfully.");
    } catch (error) {
      setResendLabel(error.message || "Failed to send.");
    }
  }

  async function handleRegister(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const fields = new FormData(form);
    setBusy(true);
    setRegisterError("");
    setRegisterSuccess("");

    try {
      const token = await getRecaptchaToken("register");
      const { response, data } = await postJson("/api/auth/register", {
        userType: "Client",
        companyName: String(fields.get("companyName") || ""),
        contactName: String(fields.get("contactName") || ""),
        email: String(fields.get("email") || ""),
        mobile: String(fields.get("mobile") || ""),
        password: String(fields.get("password") || ""),
        confirmPassword: String(fields.get("confirmPassword") || ""),
        recaptchaToken: token || "skip"
      });
      if (!response.ok) throw new Error(responseMessage(data, "Registration failed."));
      setRegisterSuccess(data?.message || "Account created! Please check your email.");
      form.reset();
    } catch (error) {
      setRegisterError(error.message || "Registration failed.");
    } finally {
      setBusy(false);
    }
  }

  async function handleForgotPassword(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const fields = new FormData(form);
    setBusy(true);
    setForgotStatus("");

    try {
      const token = await getRecaptchaToken("forgot_password");
      await postJson("/api/auth/forgot-password", {
        email: String(fields.get("email") || ""),
        recaptchaToken: token || "skip"
      });
      setForgotStatus("If an account exists, a reset link has been sent.");
      form.reset();
    } catch {
      setForgotStatus("Network error. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  if (forgotMode) {
    return (
      <AuthFrame title="Reset your password" subtitle="Enter your account email and we’ll send you a reset link.">
        <form className="login-form" onSubmit={handleForgotPassword}>
          <div className="auth-field">
            <label htmlFor="forgotEmail">Email address</label>
            <input id="forgotEmail" name="email" type="email" autoComplete="email" required />
          </div>
          {forgotStatus && <div className="auth-success" role="status">{forgotStatus}</div>}
          <button className="auth-primary-btn" disabled={busy} type="submit">
            {busy ? "Sending..." : "Send Reset Link"}
          </button>
          <button className="react-text-button" type="button" onClick={() => setForgotMode(false)}>
            Back to login
          </button>
        </form>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame title="Welcome" subtitle="Sign in to your workspace or register a new client account.">
      <div className="auth-tabs" role="tablist" aria-label="Account access">
        <button
          className={`auth-tab ${mode === "login" ? "active" : ""}`}
          type="button"
          role="tab"
          aria-selected={mode === "login"}
          onClick={() => setMode("login")}
        >
          Login
        </button>
        <button
          className={`auth-tab ${mode === "register" ? "active" : ""}`}
          type="button"
          role="tab"
          aria-selected={mode === "register"}
          onClick={() => setMode("register")}
        >
          Register
        </button>
      </div>

      {mode === "login" ? (
        <form className="auth-form active login-form" onSubmit={handleLogin}>
          <div className="auth-field">
            <label htmlFor="loginUser">Email or username</label>
            <input id="loginUser" name="userName" autoComplete="username" required />
          </div>
          <div className="auth-field">
            <label htmlFor="loginPass">Password</label>
            <input id="loginPass" name="password" type="password" autoComplete="current-password" required />
          </div>
          <div className="react-auth-actions">
            <button className="react-text-button" type="button" onClick={() => setForgotMode(true)}>
              Forgot password?
            </button>
          </div>
          {loginError && (
            <div className="auth-error" role="alert">
              <div>{loginError}</div>
              {confirmationEmail && (
                <button className="react-text-button" type="button" onClick={resendConfirmation}>
                  {resendLabel}
                </button>
              )}
            </div>
          )}
          <button className="auth-primary-btn" disabled={busy} type="submit">
            {busy ? "Verifying..." : "Log in"}
          </button>
        </form>
      ) : (
        <form className="auth-form active" onSubmit={handleRegister}>
          <div className="auth-field">
            <label htmlFor="regCompany">Company name</label>
            <input id="regCompany" name="companyName" autoComplete="organization" required />
          </div>
          <div className="auth-field-inline">
            <div className="auth-field">
              <label htmlFor="regRole">Contact name</label>
              <input id="regRole" name="contactName" autoComplete="name" />
            </div>
            <div className="auth-field">
              <label htmlFor="regMobile">Mobile</label>
              <input id="regMobile" name="mobile" type="tel" autoComplete="tel" />
            </div>
          </div>
          <div className="auth-field">
            <label htmlFor="regEmail">Email address</label>
            <input id="regEmail" name="email" type="email" autoComplete="email" required />
          </div>
          <div className="auth-field-inline">
            <div className="auth-field">
              <label htmlFor="regPass">Password</label>
              <input id="regPass" name="password" type="password" minLength="6" autoComplete="new-password" required />
            </div>
            <div className="auth-field">
              <label htmlFor="regPassConf">Confirm password</label>
              <input id="regPassConf" name="confirmPassword" type="password" minLength="6" autoComplete="new-password" required />
            </div>
          </div>
          {registerError && <div className="auth-error" role="alert">{registerError}</div>}
          {registerSuccess && <div className="auth-success" role="status">{registerSuccess}</div>}
          <button className="auth-primary-btn" disabled={busy} type="submit">
            {busy ? "Creating..." : "Create account"}
          </button>
        </form>
      )}

      <PortalPicker roles={portalRoles} />
    </AuthFrame>
  );
}
